import React, { useState, useEffect, useContext } from 'react';
import { Users, Plus, Search, UserPlus } from 'lucide-react';

import { ThemeContext } from "../theme";
import { LanguageContext } from "../LanguageContext";

import { text } from './constants/translations';
import { getGroupStyles } from './styles/groupStyles';

import useResponsive from './hooks/useResponsive';
import useGroups from './hooks/useGroups';
import useEmployees from './hooks/useEmployees';
import { useProfile } from '../../hooks/useProfile';

import AlertMessage from './components/AlertMessage';
import GroupCard from './components/GroupCard';
import CreateGroupModal from './components/CreateGroupModal';
import AssignModal from './components/AssignModal';

function GroupsManagement() {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();

  const { screenSize, spacing, fonts } = useResponsive();

  const [userRole, setUserRole] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("member");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);

  const {
    groups,
    allGroups,
    createdGroups,
    loading,
    error,
    success,
    setError,
    setSuccess,
    fetchMemberOfGroups,
    fetchAllGroups,
    fetchCreatedGroups,
    handleCreateGroup,
    handleAssignEmployees,
  } = useGroups(userRole, language);

  const { availableEmployees, fetchAvailableEmployees } = useEmployees(userRole);

  const styles = getGroupStyles({ theme, language, screenSize, spacing, fonts });
  const t = text[language];

  useEffect(() => {
    fetchMemberOfGroups();
  }, []);

  useEffect(() => {
    if (!profile) return;
    const role = profile.role;
    setUserRole(role);
    if (role === "admin" || role === "manager") {
      fetchAllGroups();
    }
  }, [profile]);

  useEffect(() => {
    if (activeTab === "member") {
      fetchMemberOfGroups();
    } else if (activeTab === "created" && (userRole === "admin" || userRole === "manager")) {
      fetchCreatedGroups();
    }
  }, [activeTab]);

  const getFilteredGroups = () => {
    const dataSource = activeTab === "member" ? groups : createdGroups;
    return dataSource.filter(group =>
      group.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.creatorSr && group.creatorSr.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  return (
    <div style={styles.container}>
      {error && (
        <AlertMessage
          type="error"
          message={error}
          onClose={() => setError("")}
          fonts={fonts}
        />
      )}
      {success && (
        <AlertMessage
          type="success"
          message={success}
          onClose={() => setSuccess("")}
          fonts={fonts}
        />
      )}

      {/* Header */}
      <div style={styles.card1}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.title}>
              {/* <Users size={28} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} /> */}
              {t.title}
            </h1>
            {/* {(userRole === "admin" || userRole === "manager") && (
              <p style={styles.subtitle}>{t.subtitle}</p>
            )} */}
          </div>

          <div style={styles.buttonGroup}>
            {(userRole === "admin" || userRole === "manager") && (
              <>
                <button
                  style={styles.createBtn}
                  onClick={() => setShowCreateModal(true)}
                // onMouseEnter={(e) => {
                //   e.currentTarget.style.transform = "translateY(-2px)";
                //   e.currentTarget.style.boxShadow = "0 0.5rem 1.5rem rgba(46, 124, 186, 0.4)";
                // }}
                // onMouseLeave={(e) => {
                //   e.currentTarget.style.transform = "translateY(0)";
                //   e.currentTarget.style.boxShadow = "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)";
                // }}
                >
                  <Plus size={20} />
                  {t.createGroup}
                </button>

                <button
                  style={{ ...styles.createBtn, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                  onClick={() => {
                    fetchAvailableEmployees();
                    setShowAssignModal(true);
                  }}
                // onMouseEnter={(e) => {
                //   e.currentTarget.style.transform = "translateY(-2px)";
                //   e.currentTarget.style.boxShadow = "0 0.5rem 1.5rem rgba(16, 185, 129, 0.4)";
                // }}
                // onMouseLeave={(e) => {
                //   e.currentTarget.style.transform = "translateY(0)";
                //   e.currentTarget.style.boxShadow = "0 0.25rem 0.75rem rgba(16, 185, 129, 0.3)";
                // }}
                >
                  <UserPlus size={20} />
                  {t.assignEmployees}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      

      {/* Search */}
      {/* <div style={styles.searchContainer}>
        <div style={{ position: 'relative', marginBottom: '-1rem' , marginTop: '-1rem', marginLeft: '-1rem' }}>
          <style>{`
            input[data-search="group-search"]::placeholder { color: ${theme === 'dark' ? "rgba(255,255,255,0.7)" : "#0f172a"}; opacity: 0.5; }
          `}</style>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '1.5rem',
              marginLeft: '-0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.5
            }}
          />
          <input
            type="text"
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-search="group-search"
            style={{ ...styles.searchInput, paddingLeft: '3rem', }}
          />
        </div>
      </div> */}

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "member" ? styles.activeTab : {}),
            ...(hoveredTab === "member" && activeTab !== "member" ? {
              background: theme === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.04)",
              color: theme === "dark"
                ? "rgba(255,255,255,0.75)"
                : "rgba(26,26,26,0.8)",
              transform: "translateY(-2px)",
            } : {}),
          }}
          onClick={() => setActiveTab("member")}
          onMouseEnter={() => setHoveredTab("member")}
          onMouseLeave={() => setHoveredTab(null)}
        >
          {t.memberOfGroups}
        </button>
        {(userRole === "admin" || userRole === "manager") && (
          <button
            style={{
              ...styles.tab,
              ...(activeTab === "created" ? styles.activeTab : {}),
              ...(hoveredTab === "created" && activeTab !== "created" ? {
                background: theme === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.04)",
                color: theme === "dark"
                  ? "rgba(255,255,255,0.75)"
                  : "rgba(26,26,26,0.8)",
                transform: "translateY(-2px)",
              } : {}),
            }}
            onClick={() => setActiveTab("created")}
            onMouseEnter={() => setHoveredTab("created")}
            onMouseLeave={() => setHoveredTab(null)}
          >
            {t.createdGroups}
          </button>
        )}
      </div>

      {/* Groups Grid */}
      {loading ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: fonts.subtitle }}>{t.loading}</div>
        </div>
      ) : getFilteredGroups().length === 0 ? (
        <div style={styles.emptyState}>
          <Users size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
          <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
            {activeTab === "member" ? t.noGroups : t.noCreatedGroups}
          </div>
          <div style={{ fontSize: fonts.body, marginTop: "0.5rem" }}>
            {activeTab === "member" ? t.noGroupsMessage : t.noCreatedGroupsMessage}
          </div>
        </div>
      ) : (
        <div style={styles.groupsGrid}>
          {getFilteredGroups().map((group) => (
            <GroupCard
              key={group.groupId || group.id}
              group={group}
              activeTab={activeTab}
              theme={theme}
              fonts={fonts}
              styles={styles}
            />
          ))}
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <CreateGroupModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateGroup}
          loading={loading}
          text={t}
          styles={styles}
          spacing={spacing}
        />
      )}

      {/* Assign Employees Modal */}
      {showAssignModal && (
        <AssignModal
          onClose={() => setShowAssignModal(false)}
          onSubmit={handleAssignEmployees}
          loading={loading}
          text={t}
          styles={styles}
          spacing={spacing}
          fonts={fonts}
          allGroups={allGroups}
          groups={groups}
          userRole={userRole}
          availableEmployees={availableEmployees}
        />
      )}
    </div>
  );
}

export default GroupsManagement;